/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { useLocale } from "@superweb/intl";
import { brandIcons, proServiceIcons, serviceIcons } from "@superweb/ui";

type RoughSpecification = "taxi" | "eats" | "smena" | "delivery";

const brandSpecificationIcons = {
  taxi: serviceIcons.Taxi,
  taxiYango: brandIcons.YandexEN,
  delivery: serviceIcons.Delivery,
  deliveryYango: serviceIcons.Deli,
  eats: serviceIcons.Eats,
  smena: proServiceIcons.Smena,
};

const specificationOrder = {
  taxi: 1,
  delivery: 2,
  eats: 3,
  smena: 4,
};

const getRoughSpecification = (
  specification: string,
): RoughSpecification | undefined => {
  switch (specification) {
    case "taxi":
    case "smena":
    case "delivery":
      return specification;
    case "eda":
      return "eats";
    default:
      return undefined;
  }
};

const getSpecificationIcon = (
  specification: RoughSpecification,
  brand: string,
) => {
  switch (specification) {
    case "taxi":
      return brand === "yango"
        ? brandSpecificationIcons.taxiYango
        : brandSpecificationIcons.taxi;
    case "delivery":
      return brand === "yango"
        ? brandSpecificationIcons.deliveryYango
        : brandSpecificationIcons.delivery;
    case "eats":
    case "smena":
      return brandSpecificationIcons[specification];
  }
};

export const Specifications = ({
  specifications,
  brand,
  size = "m",
}: {
  specifications: string[];
  brand: string;
  size?: "s" | "m";
}) => {
  const {
    textInfo: { direction },
  } = useLocale();

  return (
    <ul
      css={{
        display: "flex",
        width: "min-content",
        marginInlineStart: "4px",
        marginBlockStart: "4px",
        direction: direction === "ltr" ? "rtl" : "ltr",
      }}
    >
      {specifications
        .map(getRoughSpecification)
        .flatMap((spec) => spec || [])
        .sort((a, b) => specificationOrder[b] - specificationOrder[a])
        .map((name) => (
          <SpecificationIcon
            key={name}
            specification={name}
            brand={brand}
            size={size}
          />
        ))}
    </ul>
  );
};

const SpecificationIcon = ({
  specification,
  brand,
  size,
}: {
  specification: RoughSpecification;
  brand: string;
  size?: "s" | "m";
}) => {
  const Icon = getSpecificationIcon(specification, brand);

  return (
    <li css={{ marginInlineStart: "-4px" }}>
      <Icon
        aria-label={specification}
        width={size === "m" ? 20 : 14}
        height={size === "m" ? 20 : 14}
      />
    </li>
  );
};
